import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

/* --------------------------------- icons --------------------------------- */
/* Inline SVGs — datanodes doesn't depend on an icon library; matches the
   existing Tailwind dark theme with `text-primary` accents. */
const Icon = ({ d, className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
       strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const I = {
  rocket:   () => <Icon d="M5 13l-1.5 4.5L8 16l4.5-1.5L11 19l-3 3 5-1 7-7-3-3-7 7 1-5-3-3z" />,
  layers:   () => <Icon d={["M12 2 2 7l10 5 10-5z", "M2 17l10 5 10-5", "M2 12l10 5 10-5"]} />,
  key:      () => <Icon d={["M21 2l-9.6 9.6", "M15.5 7.5l4 4", "M3 21l6.6-6.6", "M9 9a4 4 0 1 0 6 6 4 4 0 0 0-6-6z"]} />,
  network:  () => <Icon d={["M5 12V7l7-4 7 4v5", "M5 12l7 4 7-4", "M12 16v6"]} />,
  zap:      () => <Icon d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />,
  chart:    () => <Icon d={["M3 3v18h18", "M7 14l4-4 3 3 5-5"]} />,
  card:     () => <Icon d={["M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M3 10h18"]} />,
  plug:     () => <Icon d={["M9 2v4", "M15 2v4", "M5 8h14l-1 6a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4z", "M12 18v4"]} />,
  chevron:  () => <Icon d="M9 6l6 6-6 6" className="w-3 h-3" />,
  copy:     () => <Icon d={["M9 9h11v11H9z", "M5 5h11v4", "M5 5v11h4"]} />,
  arrow:    () => <Icon d={["M5 12h14", "M13 5l7 7-7 7"]} />,
  back:     () => <Icon d={["M19 12H5", "M11 19l-7-7 7-7"]} />,
};

/* ---------------------------------- nav ---------------------------------- */

/* Section accents pulled from the nodefleet brand palette
   (tailwind.config.js): primary cyan #49B7F8 + morado purples
   #2A274B / #1E1B3C / #16142D. Two-color rotation keeps the page
   on-brand without the rainbow chaos. */
const sections = [
  { id: "getting-started", label: "Getting started",  Icon: I.rocket,  color: "cyan"   },
  { id: "dashboard",       label: "Dashboard layout", Icon: I.layers,  color: "morado" },
  { id: "api-keys",        label: "API keys",         Icon: I.key,     color: "cyan"   },
  { id: "requests",        label: "Making requests",  Icon: I.zap,     color: "morado" },
  { id: "chains",          label: "Available chains", Icon: I.network, color: "cyan"   },
  { id: "plans",           label: "Plans & limits",   Icon: I.card,    color: "morado" },
  { id: "request-weights", label: "Request weights",  Icon: I.zap,     color: "cyan"   },
  { id: "analytics",       label: "Analytics",        Icon: I.chart,   color: "morado" },
  { id: "mcp",             label: "MCP integration",  Icon: I.plug,    color: "cyan"   },
];

const accent = {
  cyan:   {
    // text: #49B7F8 (primary), bg/border tinted from primary at low alpha
    bg: "bg-primary/10",
    text: "text-primary",
    border: "border-primary/25",
    hover: "bg-primary/15",
    chip: "bg-primary/10 border-primary/30 text-primary",
  },
  morado: {
    // morado2 = #1E1B3C, morado1 = #2A274B — use rgba inline since
    // tailwind doesn't have a /10 modifier for arbitrary morado values
    bg: "bg-[rgba(115,103,240,0.10)]",
    text: "text-[#a5a0e6]",
    border: "border-[rgba(115,103,240,0.30)]",
    hover: "bg-[rgba(115,103,240,0.18)]",
    chip: "bg-[rgba(115,103,240,0.10)] border-[rgba(115,103,240,0.30)] text-[#a5a0e6]",
  },
};

/* --------------------------------- atoms --------------------------------- */

const Step = ({ n, children }) => (
  <div className="flex gap-3 items-start">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">{n}</span>
    <p className="text-sm text-gray-300 leading-relaxed">{children}</p>
  </div>
);

const Tip = ({ children }) => (
  <div className="flex items-start gap-2.5 p-3 rounded-sm bg-primary/[0.07] border border-primary/20">
    <span className="text-primary"><I.zap /></span>
    <p className="text-xs text-primary/85 leading-relaxed">{children}</p>
  </div>
);

const Warn = ({ children }) => (
  <div className="flex items-start gap-2.5 p-3 rounded-sm bg-[rgba(115,103,240,0.07)] border border-[rgba(115,103,240,0.25)]">
    <p className="text-xs text-[#c5c0f0] leading-relaxed">{children}</p>
  </div>
);

const Code = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(typeof children === "string" ? children : children?.props?.children || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard API not available */ }
  };
  return (
    <div className="relative">
      <pre className="mt-2 p-3 pr-12 rounded-sm bg-black/60 border border-white/[0.06] text-[12px] text-gray-300 font-mono overflow-x-auto whitespace-pre-wrap">
        {children}
      </pre>
      <button
        type="button"
        onClick={onCopy}
        className="absolute top-2 right-2 px-2 py-1 rounded-sm border border-white/10 bg-black/40 text-[10px] uppercase tracking-wider text-gray-400 hover:text-white hover:border-white/20 transition-colors"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-landing-section border border-white/[0.06] rounded-sm p-4 ${className}`}>{children}</div>
);

const Section = ({ id, color, Icon, title, children }) => {
  const c = accent[color] || accent.cyan;
  // Subtle gradient overlay on each section header — landing-section
  // (#1B1B1B) → morado3 (#16142D) at 50% mixed gives that nodefleet
  // "deep purple-tinted dark" feel without overpowering the content.
  return (
    <motion.div id={id} variants={fadeUp}
      className="relative overflow-hidden bg-landing-section border border-white/[0.06] rounded-sm p-6 scroll-mt-24">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            color === "morado"
              ? "linear-gradient(135deg, rgba(42,39,75,0.20) 0%, rgba(0,0,0,0) 60%)"
              : "linear-gradient(135deg, rgba(73,183,248,0.08) 0%, rgba(0,0,0,0) 60%)",
        }}
        aria-hidden
      />
      <div className="relative z-10 flex items-center gap-3 mb-5">
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${c.bg} ${c.border} border`}>
          <span className={c.text}><Icon /></span>
        </div>
        <h2 className="font-title text-lg font-semibold text-white">{title}</h2>
      </div>
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
};

/* -------------------------------- chains --------------------------------- */
/* Trimmed to the 29 active production chains (from `chains.Chain.active=True`
   on staging k8s). Group by chain family for readability. Source of truth
   is the API (`/api/chains/chains/`); update if that diverges. */
const CHAINS_EVM = [
  "eth-mainnet", "arbitrum", "avax", "base", "blast", "bsc",
  "frax-mainnet", "ink", "linea", "mantle", "metis-mainnet",
  "oasys", "optimism", "unichain",
];
const CHAINS_COSMOS = [
  "akash-mainnet", "akash-mainnet-rest",
  "axelar-mainnet", "axelar-mainnet-rest",
  "lava-mainnet", "lava-mainnet-rest",
  "osmosis", "osmosis-rest",
  "kava-rest",
];

/* -------------------------------- MCP tools ------------------------------ */
/* Update if the MCP server tool catalog changes. */
const MCP_TOOLS = [
  { cat: "Chains",       name: "list_chains",            desc: "List active chains with rps cap, archive support, and pricing." },
  { cat: "Chains",       name: "get_chain_status",       desc: "Latest health for a chain — block height, latency, sync state." },
  { cat: "Requests",     name: "rpc_call",               desc: "Make a JSON-RPC call (any method) against your default key on a chain." },
  { cat: "Requests",     name: "evm_get_balance",        desc: "Read native-token balance for an EVM address." },
  { cat: "Requests",     name: "evm_get_block",          desc: "Fetch a block by number or hash." },
  { cat: "Requests",     name: "evm_get_transaction",    desc: "Fetch a transaction + receipt by hash." },
  { cat: "Requests",     name: "cosmos_get_balance",     desc: "Read balances on a Cosmos chain (REST)." },
  { cat: "Requests",     name: "cosmos_get_block",       desc: "Fetch a Cosmos block by height." },
  { cat: "Requests",     name: "cosmos_get_staking",     desc: "Validator + delegation info on a Cosmos chain." },
  { cat: "Requests",     name: "solana_get_balance",     desc: "Read SOL balance for a Solana address." },
  { cat: "Account",      name: "list_api_keys",          desc: "List your org's keys with status + per-key analytics." },
  { cat: "Account",      name: "get_subscription",       desc: "Current plan, RPS, monthly cap, daily/monthly used." },
  { cat: "Account",      name: "get_usage",              desc: "Live quota counters for the current org." },
];
const MCP_GROUPS = ["Chains", "Requests", "Account"];
const MCP_BY_GROUP = MCP_GROUPS.map((g) => ({
  title: g,
  items: MCP_TOOLS.filter((t) => t.cat === g),
}));

/* ============================== component ================================ */

const Documentation = () => {
  const [active, setActive] = useState("getting-started");

  useEffect(() => { document.title = "Documentation — DataNodes"; }, []);

  const goto = (id) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <div>
            <h1 className="font-title text-3xl sm:text-4xl font-bold text-white">Documentation</h1>
            <p className="font-secondary text-sm text-gray-400 mt-1">
              Learn how to use DataNodes — high-performance blockchain RPC + REST gateway.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/dashboard"
              className="font-secondary inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-white/[0.08] bg-landing-section text-sm text-gray-300 hover:text-white hover:border-white/20 transition-colors"
            >
              Open dashboard <I.arrow />
            </Link>
          </div>
        </div>

        {/* Two-column: sticky left sidebar nav + main content. On mobile,
            sidebar collapses to a top horizontal pill row. */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar nav (lg+) */}
          <aside className="hidden lg:block w-60 shrink-0">
            <nav className="sticky top-6 space-y-1">
              <p className="font-secondary text-[10px] uppercase tracking-[0.18em] text-gray-500 px-3 mb-3">
                On this page
              </p>
              {sections.map((s) => {
                const c = accent[s.color];
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => goto(s.id)}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] font-medium border-l-2 transition-all ${
                      isActive
                        ? `${c.text} border-l-current bg-white/[0.03]`
                        : "text-gray-400 border-l-transparent hover:text-white hover:bg-white/[0.02]"
                    }`}
                  >
                    <span className={isActive ? c.text : "text-gray-500"}><s.Icon /></span>
                    {s.label}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Mobile pill nav */}
          <div className="flex flex-wrap gap-2 mb-2 lg:hidden">
            {sections.map((s) => {
              const c = accent[s.color];
              const isActive = active === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => goto(s.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium border transition-all ${
                    isActive
                      ? `${c.hover} ${c.border} ${c.text}`
                      : "bg-landing-section border-white/[0.06] text-gray-400 hover:text-white hover:border-white/20"
                  }`}
                >
                  <span className={isActive ? c.text : "text-gray-500"}><s.Icon /></span>
                  {s.label}
                </button>
              );
            })}
          </div>

        {/* Main column */}
        <motion.div variants={stagger} initial="hidden" animate="show" className="flex-1 min-w-0 space-y-5 pb-12">

          {/* Getting started */}
          <Section id="getting-started" color="primary" Icon={I.rocket} title="Getting started">
            <p className="font-secondary text-sm text-gray-400 mb-5">
              DataNodes is an RPC + REST gateway for blockchain nodes. Sign up,
              create an API key, send your first request — that's the whole
              flow. Free tier included; no credit card required.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Card>
                <h3 className="text-sm font-semibold text-white mb-3">Sign up with email</h3>
                <div className="space-y-2.5">
                  <Step n={1}>Go to <Link to="/signup" className="text-primary underline underline-offset-2">/signup</Link>.</Step>
                  <Step n={2}>Enter email, password, and your name. Verify your email when prompted.</Step>
                  <Step n={3}>You're auto-enrolled in the <span className="text-primary font-medium">Free</span> plan and a personal workspace is created.</Step>
                </div>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-white mb-3">Sign up with Google</h3>
                <div className="space-y-2.5">
                  <Step n={1}>Click <span className="text-white font-medium">Continue with Google</span> on the login page.</Step>
                  <Step n={2}>Approve the OAuth consent. Same Free plan + workspace get provisioned automatically.</Step>
                  <Step n={3}>You land on the dashboard ready to create your first project + API key.</Step>
                </div>
              </Card>
            </div>
            <Tip>The Free plan gives you 25 RPS, 500K full-node + 100K archive-node requests per month, and access to all active chains. Upgrade only when you outgrow it.</Tip>
          </Section>

          {/* Dashboard layout */}
          <Section id="dashboard" color="amber" Icon={I.layers} title="Dashboard layout">
            <p className="font-secondary text-sm text-gray-400 mb-5">
              Once signed in, the app gives you four main views in the left sidebar.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <h3 className="text-sm font-semibold text-white mb-2">Dashboard</h3>
                <p className="text-xs text-gray-400">
                  Total requests (last 30d), monthly usage with progress bar, daily usage, and quick links to your projects + keys.
                </p>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-white mb-2">Nodes</h3>
                <p className="text-xs text-gray-400">
                  Per-chain status (active/inactive). Toggle which chains your project should have access to.
                </p>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-white mb-2">Analytics</h3>
                <p className="text-xs text-gray-400">
                  Per-key analytics: 30-day chart, status-code breakdown, top chains by traffic. Live quota counter agrees with what the gateway enforces.
                </p>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-white mb-2">Settings</h3>
                <p className="text-xs text-gray-400">
                  Current plan, upgrade flow, billing history, organization members + invites, profile.
                </p>
              </Card>
            </div>
          </Section>

          {/* API keys */}
          <Section id="api-keys" color="violet" Icon={I.key} title="API keys">
            <p className="font-secondary text-sm text-gray-400 mb-5">
              API keys authenticate every request. Create as many as you need
              per project — limits are enforced at the <span className="text-white font-medium">organization</span> level,
              not per key, so adding more keys won't bypass your plan cap.
            </p>
            <div className="space-y-3 mb-5">
              <Step n={1}>Create or pick a project on the Dashboard.</Step>
              <Step n={2}>Click <span className="text-primary font-medium">+ Create API Key</span> and give it a name (e.g. "Production", "Staging").</Step>
              <Step n={3}>Copy the key. It looks like <code className="text-primary text-[11px]">sk_live_…</code>. The full key is shown <span className="text-primary">once</span>.</Step>
              <Step n={4}>Put the key in your RPC URL — <code className="text-white">/&lt;chain&gt;/&lt;key&gt;</code> — or send it via the <code className="text-white">x-api-key</code> header.</Step>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <Card><h4 className="text-xs uppercase tracking-wider text-gray-500 mb-1">Regenerate</h4><p className="text-xs text-gray-400">Rotates the secret. Old key stops working immediately.</p></Card>
              <Card><h4 className="text-xs uppercase tracking-wider text-gray-500 mb-1">Deactivate</h4><p className="text-xs text-gray-400">Disables the key without deleting it. Reactivatable later.</p></Card>
              <Card><h4 className="text-xs uppercase tracking-wider text-gray-500 mb-1">Analytics</h4><p className="text-xs text-gray-400">Per-key request charts + status-code breakdowns over 1d / 7d / 30d.</p></Card>
            </div>
            <Warn>Treat keys like passwords. Don't commit them. Rotate immediately if exposed in logs, public repos, or screenshots.</Warn>
          </Section>

          {/* Making requests */}
          <Section id="requests" color="blue" Icon={I.zap} title="Making requests">
            <p className="font-secondary text-sm text-gray-400 mb-5">
              One endpoint, every chain: put the chain slug and your key in the
              URL — <span className="text-white font-medium">the same pattern you know from other RPC providers</span>.
              Prefer headers? That works too.
            </p>

            <h3 className="text-sm font-semibold text-white mt-2 mb-2">URL form (recommended)</h3>
            <p className="text-xs text-gray-400 mb-2">
              <code>{`https://rpc.datanodes.nodefleet.net/<chain>/<key>`}</code> — drop it into any
              wallet, SDK, or script that takes an RPC URL. Swap the chain slug, keep everything else.
            </p>
            <Code>{`curl -X POST 'https://rpc.datanodes.nodefleet.net/eth-mainnet/sk_live_…' \\
  -H 'Content-Type: application/json' \\
  -d '{"jsonrpc":"2.0","method":"eth_blockNumber","params":[],"id":1}'`}</Code>
            <p className="text-[11px] text-gray-500 mt-2 mb-0">
              <code>/v1/&lt;chain&gt;/&lt;key&gt;/</code> works identically. A trailing slash is optional in both.
            </p>

            <h3 className="text-sm font-semibold text-white mt-5 mb-2">Header form</h3>
            <p className="text-xs text-gray-400 mb-2">
              Keeps the key out of the URL (and out of any intermediary access logs). Pass the chain slug
              in <code>x-chain</code> and the key in <code>x-api-key</code>. Same URL for every chain.
            </p>
            <Code>{`curl -X POST 'https://rpc.datanodes.nodefleet.net/' \\
  -H 'x-api-key: sk_live_…' \\
  -H 'x-chain: eth-mainnet' \\
  -H 'Content-Type: application/json' \\
  -d '{"jsonrpc":"2.0","method":"eth_blockNumber","params":[],"id":1}'`}</Code>

            <h3 className="text-sm font-semibold text-white mt-5 mb-2">Cosmos REST example</h3>
            <p className="text-xs text-gray-400 mb-2">
              REST chains use the <code>-rest</code> suffix. With the URL form, append the REST
              resource path after your key; with headers, request the resource path directly.
            </p>
            <Code>{`# URL form — resource path goes after the key
curl 'https://rpc.datanodes.nodefleet.net/osmosis-rest/sk_live_…/cosmos/staking/v1beta1/pool'

# Header form
curl -H 'x-api-key: sk_live_…' -H 'x-chain: osmosis-rest' \\
  'https://rpc.datanodes.nodefleet.net/cosmos/staking/v1beta1/pool'`}</Code>

            <h3 className="text-sm font-semibold text-white mt-5 mb-2">Response headers</h3>
            <Card>
              <ul className="space-y-1.5 text-xs text-gray-400">
                <li><code className="text-primary">x-datanodes-cu-units</code> — request cost in CU (1 unit = 1 normal request, archive ≈ 6×).</li>
                <li><code className="text-primary">x-datanodes-cu-bucket</code> — <code>full</code>, <code>archive</code>, or <code>special_*</code>.</li>
                <li><code className="text-primary">x-datanodes-cu-mcp</code> — <code>1</code> if request was tagged as MCP traffic (extra surcharge).</li>
              </ul>
            </Card>

            <Tip>Errors come back as JSON-RPC <code>-32005</code> on rate-limit hits ("Rate limit exceeded: 25 RPS limit" or "Monthly limit exceeded"). Check headers for cost details before retrying.</Tip>
          </Section>

          {/* Available chains */}
          <Section id="chains" color="emerald" Icon={I.network} title="Available chains">
            <p className="font-secondary text-sm text-gray-400 mb-5">
              All 29 chains below are live and serving 200s on mainnet. Use the slug as the
              <code className="ml-1">x-chain</code> header value. Hit <code>/api/chains/chains/</code> for the
              authoritative live list.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <h3 className="text-xs uppercase tracking-wider text-primary mb-3">EVM mainnets ({CHAINS_EVM.length})</h3>
                <div className="flex flex-wrap gap-1.5">
                  {CHAINS_EVM.map((slug) => (
                    <code key={slug} className="px-2 py-0.5 rounded-sm bg-primary/10 border border-primary/25 text-[11px] text-primary font-mono">{slug}</code>
                  ))}
                </div>
              </Card>
              <Card>
                <h3 className="text-xs uppercase tracking-wider text-[#a5a0e6] mb-3">Cosmos / Tendermint ({CHAINS_COSMOS.length})</h3>
                <div className="flex flex-wrap gap-1.5">
                  {CHAINS_COSMOS.map((slug) => (
                    <code key={slug} className="px-2 py-0.5 rounded-sm bg-[rgba(115,103,240,0.10)] border border-[rgba(115,103,240,0.30)] text-[11px] text-[#c5c0f0] font-mono">{slug}</code>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-3">
                  Tendermint chains accept JSON-RPC at <code>/</code>. REST chains (<code>-rest</code> suffix) accept Cosmos REST paths like <code>/cosmos/...</code>.
                </p>
              </Card>
            </div>
          </Section>

          {/* Plans & limits */}
          <Section id="plans" color="sky" Icon={I.card} title="Plans & rate limits">
            <p className="font-secondary text-sm text-gray-400 mb-5">
              Quota is enforced at the <span className="text-white font-medium">organization</span> level.
              All your keys share one bucket — adding keys won't bypass the cap.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              <Card>
                <h3 className="text-sm font-semibold text-white mb-1">Free</h3>
                <p className="text-xs text-gray-500 mb-2">$0 / month</p>
                <ul className="space-y-1 text-xs text-gray-400">
                  <li>• 25 RPS</li>
                  <li>• 500K req / month</li>
                  <li>• 100K archive req / month</li>
                  <li>• MCP +20% surcharge</li>
                </ul>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-white mb-1">Builder Fleet</h3>
                <p className="text-xs text-gray-500 mb-2">$25 / month <span className="text-gray-600">· $0.83 / 1M</span></p>
                <ul className="space-y-1 text-xs text-gray-400">
                  <li>• 75 RPS</li>
                  <li>• 30M req / month</li>
                  <li>• 10M archive req / month</li>
                  <li>• Hard-capped (no overage)</li>
                </ul>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-white mb-1">Growth Fleet</h3>
                <p className="text-xs text-gray-500 mb-2">$79 / month <span className="text-gray-600">· $0.66 / 1M</span></p>
                <ul className="space-y-1 text-xs text-gray-400">
                  <li>• 175 RPS</li>
                  <li>• 120M req / month</li>
                  <li>• 40M archive req / month</li>
                  <li>• Overage $0.85/M full · $1.50/M archive</li>
                  <li>• Global load balancer</li>
                </ul>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-white mb-1">Pro Fleet</h3>
                <p className="text-xs text-gray-500 mb-2">$199 / month <span className="text-gray-600">· $0.50 / 1M</span></p>
                <ul className="space-y-1 text-xs text-gray-400">
                  <li>• 300 RPS</li>
                  <li>• 400M req / month</li>
                  <li>• 120M archive req / month</li>
                  <li>• Overage $0.85/M full · $1.50/M archive</li>
                  <li>• Global load balancer</li>
                </ul>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-white mb-1">Custom</h3>
                <p className="text-xs text-gray-500 mb-2">Contact sales</p>
                <ul className="space-y-1 text-xs text-gray-400">
                  <li>• Custom RPS / monthly</li>
                  <li>• Dedicated nodes</li>
                  <li>• SSO + custom SLAs</li>
                  <li>
                    <a href="mailto:nodes@nodefleet.net" className="text-primary underline underline-offset-2">nodes@nodefleet.net</a>
                  </li>
                </ul>
              </Card>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Switch plans from <Link to="/settings" className="text-primary underline underline-offset-2">Settings → Plans</Link>.
              Stripe handles checkout. Free is your starting plan; downgrade anytime by canceling a paid sub.
              Overage on Growth + Pro requires turning on <span className="text-white">Extra Usage</span> and topping up a prepaid wallet.
            </p>
          </Section>

          {/* Request weights — what counts as 1 vs N units against your plan */}
          <Section id="request-weights" color="cyan" Icon={I.zap} title="Request weights">
            <p className="font-secondary text-sm text-gray-400 mb-4">
              Not every request costs the same. Your plan's monthly cap is denominated
              in <span className="text-white font-medium">CU (compute units)</span>, and
              individual chains / methods can consume more than one CU per call.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
              <Card>
                <h3 className="text-sm font-semibold text-white mb-2">Standard chains</h3>
                <ul className="space-y-1.5 text-xs text-gray-400">
                  <li>• 1 full-node call = <span className="text-white">1 CU</span></li>
                  <li>• 1 archive call counts against your <span className="text-white">separate archive bucket</span>, not the full-node bucket</li>
                </ul>
                <p className="text-xs text-gray-500 mt-3">
                  Each plan ships two independent monthly caps — full-node and archive — so heavy archive usage on Builder/Growth/Pro doesn't burn through your standard quota.
                </p>
              </Card>

              <Card className="border-amber-500/25 bg-amber-500/[0.04]">
                <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                  Solana
                  <span className="rounded-sm border border-amber-500/30 bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-200">
                    2× multiplier
                  </span>
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed mb-2">
                  Solana RPC is the only chain with a per-chain weight multiplier today. Every Solana request
                  — regardless of full vs archive bucket — is counted as <span className="text-white font-medium">2 CUs</span>.
                </p>
                <ul className="space-y-1 text-xs text-gray-400">
                  <li>• 1 full-node Solana call = <span className="text-white">2 CU</span></li>
                  <li>• 1 archive Solana call = <span className="text-white">archive_weight × 2</span> CU (Free = 12 CU)</li>
                </ul>
                <p className="text-xs text-amber-200/75 mt-3 leading-relaxed">
                  <span className="font-semibold">Why?</span> Solana's higher transaction throughput and lookup cost
                  make a single call meaningfully more expensive on our side than an EVM equivalent. The multiplier
                  reflects that real-world cost so the per-chain economics balance out without inflating prices.
                </p>
              </Card>
            </div>

            <p className="font-secondary text-sm text-gray-400 mb-3">
              See the actual CU charged per request in the response headers:
            </p>
            <pre className="bg-black/40 border border-white/[0.06] rounded-sm p-3 text-xs text-gray-300 overflow-x-auto">
{`x-datanodes-cu-units: 12       # CU charged for this request
x-datanodes-cu-bucket: archive # full | archive
x-datanodes-cu-mcp: 0          # 1 if surcharged via MCP gateway`}
            </pre>

            <p className="font-secondary text-xs text-gray-500 mt-4">
              Your <Link to="/nodes" className="text-primary underline underline-offset-2">Networks list</Link> shows
              the multiplier badge next to chains that carry one. The Analytics page's "Monthly usage" counter is the
              CU-weighted total; "Full node" and "Archive node" cards show raw request counts in each bucket.
            </p>
          </Section>

          {/* Analytics */}
          <Section id="analytics" color="purple" Icon={I.chart} title="Analytics & usage">
            <p className="font-secondary text-sm text-gray-400 mb-5">
              The Analytics page tracks every key's traffic. Two layers:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <h3 className="text-sm font-semibold text-white mb-2">Live quota counters</h3>
                <p className="text-xs text-gray-400">
                  Daily / monthly used numbers come straight from the same Redis
                  counter the gateway uses to enforce limits. They update within a
                  second of every request and are <span className="text-white font-medium">authoritative</span> — when the dashboard says you're at cap, the gateway is 429-ing.
                </p>
              </Card>
              <Card>
                <h3 className="text-sm font-semibold text-white mb-2">Historical breakdowns</h3>
                <p className="text-xs text-gray-400">
                  Charts (by date, by chain, status-code split) come from DB rollups.
                  Daily granularity, slight lag (~1 hour) but high cardinality —
                  great for "which chain blew up at 03:00 UTC?" investigations.
                </p>
              </Card>
            </div>
          </Section>

          {/* MCP integration */}
          <Section id="mcp" color="morado" Icon={I.plug} title="MCP integration">
            <p className="font-secondary text-sm text-gray-400 mb-3">
              DataNodes ships a <a href="https://modelcontextprotocol.io" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">Model Context Protocol</a> server
              so any LLM-powered tool can call your blockchain RPCs natively — no copy-pasted curl commands. Works with the same <code>sk_live_…</code> key as direct API access.
            </p>
            <p className="font-secondary text-sm text-gray-400 mb-5">
              <span className="text-white font-medium">We're LLM-agnostic.</span>{" "}
              Use your favorite AI tooling — the configuration shape is identical
              across providers because they all implement the same MCP spec.
              Drop the same JSON block into whichever client you prefer.
            </p>

            <h3 className="text-sm font-semibold text-white mb-2 mt-2">Configuration (works for every MCP client)</h3>
            <p className="text-xs text-gray-400 mb-2">
              Replace <code>sk_live_...</code> with a key from your dashboard.
            </p>
            <Code>{`{
  "mcpServers": {
    "datanodes": {
      "url": "https://mcp.datanodes.nodefleet.net/mcp",
      "headers": {
        "x-api-key": "sk_live_..."
      }
    }
  }
}`}</Code>

            <h3 className="text-sm font-semibold text-white mt-5 mb-3">Where to put the config</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              <Card>
                <h4 className="text-xs uppercase tracking-wider text-primary mb-2">Claude Desktop</h4>
                <code className="text-[11px] text-gray-300 break-all">~/Library/Application Support/Claude/claude_desktop_config.json</code>
              </Card>
              <Card>
                <h4 className="text-xs uppercase tracking-wider text-[#a5a0e6] mb-2">Claude Code</h4>
                <p className="text-[11px] text-gray-400"><code>~/.claude.json</code> or per-project <code>.mcp.json</code></p>
              </Card>
              <Card>
                <h4 className="text-xs uppercase tracking-wider text-primary mb-2">Codex</h4>
                <code className="text-[11px] text-gray-300 break-all">~/.codex/config.json</code>
              </Card>
              <Card>
                <h4 className="text-xs uppercase tracking-wider text-[#a5a0e6] mb-2">Cursor</h4>
                <p className="text-[11px] text-gray-400">Settings → MCP → <span className="text-white">Add server</span> with the same JSON.</p>
              </Card>
              <Card>
                <h4 className="text-xs uppercase tracking-wider text-primary mb-2">Continue.dev</h4>
                <code className="text-[11px] text-gray-300 break-all">~/.continue/config.json</code>
              </Card>
              <Card>
                <h4 className="text-xs uppercase tracking-wider text-[#a5a0e6] mb-2">Anything else MCP-compatible</h4>
                <p className="text-[11px] text-gray-400">Same JSON shape. The <code>url</code> + <code>headers</code> spec is universal.</p>
              </Card>
            </div>

            <h3 className="text-sm font-semibold text-white mt-5 mb-3">Available tools</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {MCP_BY_GROUP.map(({ title, items }, idx) => {
                // Alternate cyan / morado per card to keep brand consistency
                const isMorado = idx % 2 === 1;
                return (
                  <Card key={title}>
                    <h4 className={`text-xs uppercase tracking-wider mb-3 ${isMorado ? "text-[#a5a0e6]" : "text-primary"}`}>{title}</h4>
                    <ul className="space-y-2.5 text-xs text-gray-400">
                      {items.map(({ name, desc }) => (
                        <li key={name} className="flex flex-col">
                          <code className={`font-mono text-[11px] ${isMorado ? "text-[#c5c0f0]" : "text-primary"}`}>{name}</code>
                          <span className="text-gray-400 mt-0.5">{desc}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                );
              })}
            </div>
            <Tip>Replace <code>sk_live_...</code> with a key from your dashboard. Quota is enforced just like direct API calls — the same per-org bucket — so MCP traffic counts toward your monthly cap.</Tip>
          </Section>

        </motion.div>
        </div>{/* /flex two-column */}

        {/* Footer */}
        <div className="border-t border-white/[0.06] pt-6 mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            Need help? Email <a href="mailto:nodes@nodefleet.net" className="text-primary underline underline-offset-2">nodes@nodefleet.net</a>.
          </p>
          <Link to="/" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1.5">
            <I.back /> Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
